import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { api } from '@services/authService';

interface ForcePasswordChangeDialogProps {
  open: boolean;
  onClose: () => void;
}

const ForcePasswordChangeDialog: React.FC<ForcePasswordChangeDialogProps> = ({ open, onClose }) => {
  const { signOut } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validari
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Toate campurile sunt obligatorii');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Parolele nu se potrivesc');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError('Parola noua trebuie sa aiba cel putin 8 caractere');
      setLoading(false);
      return;
    }

    // Validare pattern pentru parola noua (consistent cu backend-ul)
    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordPattern.test(newPassword)) {
      setError('Parola noua trebuie sa contina cel putin o litera mica, o litera mare, o cifra si un caracter special');
      setLoading(false);
      return;
    }

    if (currentPassword === newPassword) {
      setError('Parola noua trebuie sa fie diferita de parola curenta');
      setLoading(false);
      return;
    }

    try {
      // Schimba parola
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });

      // Actualizeaza flag-ul pentru parola temporara
      localStorage.removeItem('hasTemporaryPassword');
      
      // Delogheaza utilizatorul pentru a se loga din nou cu parola noua
      await signOut();
      
      // Afiseaza mesaj de succes
      alert('Parola a fost schimbata cu succes! Te rugam sa te loghezi din nou cu parola noua.');
      
    } catch (error: any) {
      console.error('Error changing password:', error);
      setError(error.response?.data?.error || 'Eroare la schimbarea parolei');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <Lock sx={{ fontSize: 40, color: 'warning.main', mr: 1 }} />
          <Typography variant="h5" fontWeight={600}>
            Schimbare Parola Obligatorie
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Ai o parola temporara si trebuie sa o schimbi inainte de a continua sa folosesti aplicatia.
          </Typography>
        </Alert>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Parola curenta (temporara)"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Parola noua"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            required
            helperText="Minim 8 caractere"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Confirma parola noua"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          fullWidth
          size="large"
        >
          {loading ? 'Se schimba parola...' : 'Schimba Parola'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForcePasswordChangeDialog;
